import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAdminOrders, updateOrderStatus, reset, getAdminOrderSummary } from '../../features/order/orderSlice';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import { formatPrice as formatCurrency } from '../../utils/currency';

const ORDERS_PER_PAGE = 20;

function AdminOrders() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showSummary, setShowSummary] = useState(false);
    const [includeHidden, setIncludeHidden] = useState(false);
    
    const { user } = useSelector((state) => state.auth);
    const { 
        orders, 
        summary,
        isLoading, 
        isError, 
        message, 
        currentPage, 
        totalPages, 
        totalOrders, 
        pageLimit,
        isSummaryLoading,
        summaryMessage
    } = useSelector((state) => state.order);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        dispatch(getAdminOrders({ page: 1, limit: ORDERS_PER_PAGE, includeUnpaidCancelled: includeHidden }));
        dispatch(getAdminOrderSummary());

        return () => {
            dispatch(reset());
        };
    }, [user, navigate, dispatch, includeHidden]);

    useEffect(() => {
        if (isError) {
            toast.error(message);
        }
    }, [isError, message]);
    
    useEffect(() => {
        if (summaryMessage) {
            toast.error(summaryMessage);
        }
    }, [summaryMessage]);

    const handleStatusChange = (orderId, newStatus) => {
        dispatch(updateOrderStatus({ orderId, status: newStatus }));
        toast.success('Order status updated');
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        dispatch(getAdminOrders({ page, limit: ORDERS_PER_PAGE, includeUnpaidCancelled: includeHidden }));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const formatPrice = (price, order) =>
        formatCurrency(price, { currency: order?.payment?.currency || order?.currency });

    const formatTotals = (totals = {}) => {
        const currencies = Object.entries(totals).filter(([, amount]) => typeof amount === 'number');
        if (!currencies.length) return '€0.00';

        return currencies
            .map(([currency, amount]) => formatCurrency(amount, { currency }))
            .join(' · ');
    };

    const renderSummaryRow = (label, data) => {
        const safeData = data || { count: 0, totals: {} };
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid var(--color-border)'
                }}
            >
                <div style={{ fontWeight: 600 }}>{label}</div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.95em', color: 'var(--color-ink)' }}>
                        {safeData.count} paid order{safeData.count === 1 ? '' : 's'}
                    </div>
                    <div style={{ color: 'var(--color-ink-soft)', fontSize: '0.9em' }}>
                        Totals: {formatTotals(safeData.totals)}
                    </div>
                </div>
            </div>
        );
    };

    const getStatusBadge = (status) => {
        const statusTokens = {
            unpaid: { bg: 'rgba(233, 130, 81, 0.18)', color: 'var(--color-accent)' },
            processing: { bg: 'rgba(0, 86, 112, 0.15)', color: 'var(--color-deep-teal)' },
            shipped: { bg: 'rgba(44, 107, 80, 0.15)', color: 'var(--color-forest)' },
            delivered: { bg: 'var(--color-deep-teal)', color: 'var(--color-surface)' },
            cancelled: { bg: 'var(--color-feedback-warn-soft)', color: 'var(--color-accent)' }
        };

        const { bg, color } = statusTokens[status] || { bg: 'var(--color-muted)', color: 'var(--color-surface)' };

        return (
            <span 
                style={{ 
                    backgroundColor: bg,
                    color,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                }}
            >
                {status.toUpperCase()}
            </span>
        );
    };

    if (isLoading && orders.length === 0) {
        return <Loading />;
    }

    return (
        <div className="admin-container">
            <h1 className="admin-header">Orders List</h1>
            <div style={{ marginBottom: '16px' }}>
                <button
                    className="btn btn-secondary"
                    onClick={() => setShowSummary((prev) => !prev)}
                    style={{ marginRight: '8px' }}
                >
                    {showSummary ? 'Hide' : 'Show'} Paid Orders Summary
                </button>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.95em' }}>
                    <input
                        type="checkbox"
                        checked={includeHidden}
                        onChange={(e) => {
                            setIncludeHidden(e.target.checked);
                            dispatch(getAdminOrders({ page: 1, limit: ORDERS_PER_PAGE, includeUnpaidCancelled: e.target.checked }));
                        }}
                    />
                    Include unpaid & cancelled
                </label>
            </div>

            {showSummary && (
                <div
                    style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '20px',
                        background: 'var(--color-surface)'
                    }}
                >
                    {isSummaryLoading && <div style={{ color: 'var(--color-ink-soft)' }}>Loading summary...</div>}
                    {!isSummaryLoading && (
                        <>
                            {renderSummaryRow('Last 24 hours', summary?.last24h)}
                            {renderSummaryRow('Last 7 days', summary?.last7d)}
                            {renderSummaryRow('Last 30 days', summary?.last30d)}
                        </>
                    )}
                </div>
            )}

            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {orders && orders.length > 0 ? (
                        orders.map((order) => (
                            <tr key={order._id}>
                                <td>#{order.order_num}</td>
                                <td>
                                    <div>
                                        <strong>{order.client?.firstName || 'N/A'} {order.client?.lastName || ''}</strong>
                                        <br />
                                        <small style={{ color: 'var(--color-ink-soft)' }}>{order.client?.email || 'No email'}</small>
                                    </div>
                                </td>
                                <td>{formatDate(order.createdAt)}</td>
                                <td>
                                    <div style={{ fontSize: '0.9em' }}>
                                        {order.orderItems.slice(0, 2).map((item, index) => (
                                            <div key={index}>
                                                {item.name} x{item.quantity}
                                            </div>
                                        ))}
                                        {order.orderItems.length > 2 && (
                                            <div style={{ color: 'var(--color-ink-soft)' }}>
                                                +{order.orderItems.length - 2} more
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <strong>{formatPrice(order.totalPrice, order)}</strong>
                                </td>
                                <td>{getStatusBadge(order.status)}</td>
                                <td>
                                    <select
                                        value={order.status}
                                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid var(--color-border)',
                                            marginRight: '8px',
                                            background: 'var(--color-surface)',
                                            color: 'var(--color-ink)'
                                        }}
                                    >
                                        <option value="unpaid">Unpaid</option>
                                        <option value="processing">Processing</option>
                                        <option value="shipped">Shipped</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    <button 
                                        className="btn btn-secondary"
                                        onClick={() => navigate(`/admin/orders/${order._id}`)}
                                        style={{ fontSize: '0.8em', padding: '4px 8px' }}
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                No orders found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="admin-pagination">
                <div>
                    {totalOrders === 0
                        ? 'No orders to display'
                        : `Showing ${(currentPage - 1) * pageLimit + 1}-${(currentPage - 1) * pageLimit + orders.length} of ${totalOrders} orders`}
                </div>
                <div className="admin-pagination-controls">
                    <button
                        className="btn btn-secondary"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage <= 1 || isLoading}
                    >
                        Previous
                    </button>
                    <span style={{ fontWeight: 600 }}>
                        Page {totalOrders === 0 ? 0 : currentPage} of {totalOrders === 0 ? 0 : totalPages}
                    </span>
                    <button
                        className="btn btn-secondary"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages || isLoading}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminOrders;
